import * as React from "react";
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";

/* DataTable row virtualization (C9). Windows the `<tbody>` so only the visible
   rows (plus overscan) are in the DOM. Returns *virtualization primitives only*
   — the rendering strategy (translateY vs spacer rows) lives in the component,
   so it can change without touching this hook's contract. */

export interface DataTableVirtual {
  /** Attach to the scroll container; it is the virtualizer's scroll element. */
  scrollRef: React.RefObject<HTMLDivElement>;
  /** The windowed rows to render (empty when `enabled` is false). */
  virtualItems: VirtualItem[];
  /** Total scrollable height of all rows, for the `<tbody>` sizing. */
  totalSize: number;
  /** Ref callback that auto-measures a rendered row, correcting the estimate. */
  measureElement: (node: Element | null) => void;
}

export interface UseDataTableVirtualOptions {
  /** When false the virtualizer is inert (count 0) — hooks still run in order. */
  enabled: boolean;
  count: number;
  estimateRowHeight: number;
  overscan: number;
}

export function useDataTableVirtual({
  enabled,
  count,
  estimateRowHeight,
  overscan,
}: UseDataTableVirtualOptions): DataTableVirtual {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // TanStack Virtual returns reactive getters by design; the component reads
  // them every render, so React Compiler memoization is correctly skipped here.
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: enabled ? count : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateRowHeight,
    overscan,
  });

  return {
    scrollRef,
    virtualItems: enabled ? virtualizer.getVirtualItems() : [],
    totalSize: virtualizer.getTotalSize(),
    measureElement: virtualizer.measureElement,
  };
}
